<template>
  <div class="relative">
    <swiper
      :slides-per-view="options.slidesPerView || 'auto'"
      :space-between="spaceBetween"
      :centered-slides="options.centeredSlides || false"
      :grab-cursor="options.grabCursor"
      :allow-touch-move="allowTouchMove"
      :auto-height="autoHeight"
      :ref="`mySwiper${id}`"
      class="mx-auto relative overflow-hidden swiper-no-swipe-back"
      :class="{ 'base-swiper-autoheight': autoHeight }"
      :options="options"
      :pagination="options.pagination || false"
      :observer="true"
      :observe-parents="true"
      @swiper="setSwiperInstance"
      @slideChange="updateIndex"
    >
      <swiper-slide v-for="(item, index) in items" :key="index" :style="slideStyle">
        <slot v-if="!loading" :item="item" :index="index"></slot>
      </swiper-slide>
      <div v-if="options.pagination" class="swiper-pagination"></div>
    </swiper>
    <div v-if="options.hasNavigation" class="swiper-controls">
      <div v-if="!atFirstPage()" class="cursor-pointer swiper-button-prev" @click="prev()">
        <slot name="prevArrow"></slot>
      </div>
      <div v-if="!atLastPage()" class="cursor-pointer swiper-button-next" @click="next()">
        <slot name="nextArrow"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import 'swiper/components/pagination/pagination.scss';
import { Swiper as SwiperCore, Pagination, Navigation, EffectCoverflow } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { ref } from 'vue';

SwiperCore.use([Pagination, Navigation, EffectCoverflow]);

export default {
  components: { Swiper, SwiperSlide },
  setup() {
    const swiper = ref(null);
    return {
      modules: [EffectCoverflow, Pagination, Navigation],
      setSwiperInstance(swiperInstance) {
        swiper.value = swiperInstance;
      },
      swiper,
    };
  },
  props: {
    id: { type: String, required: true },
    options: {
      default: () => ({
        grabCursor: true,
        centeredSlides: false,
        slidesPerView: 'auto',
        hasNavigation: false,
        pagination: false,
      }),
      type: Object,
    },
    items: { type: Array, required: true },
    gap: { type: Number, default: 12 },
    spaceBetween: { type: Number, default: 0 },
  },
  data() {
    return { isBeginning: true, isEnd: false, loading: false };
  },
  computed: {
    allowTouchMove() {
      return this.options.allowTouchMove !== false;
    },
    autoHeight() {
      return this.options.autoHeight === true;
    },
    slideStyle() {
      return {
        paddingRight: `${this.gap}px`,
      };
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.swiper) {
        this.swiper.update();
        this.updateIndex();
      }
    });
    this.startResizeObserver();
  },
  ionViewDidEnter() {
    this.$nextTick(() => {
      if (this.swiper) this.swiper.update();
    });
  },
  beforeUnmount() {
    this.stopResizeObserver();
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      this.swiper.destroy(true, true);
    }
    this.swiper = null;
  },
  methods: {
    startResizeObserver() {
      if (typeof ResizeObserver === 'undefined' || !this.$el) return;
      this._resizeObserver = new ResizeObserver(() => {
        if (this.swiper) this.swiper.update();
      });
      this._resizeObserver.observe(this.$el);
    },
    stopResizeObserver() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    },
    next() {
      if (this.swiper) this.swiper.slideNext();
    },
    prev() {
      if (this.swiper) this.swiper.slidePrev();
    },
    scrollToIndex(index) {
      if (this.swiper) this.swiper.slideTo(index);
    },
    updateIndex() {
      if (!this.swiper) return;
      this.isBeginning = this.swiper.isBeginning;
      this.isEnd = this.swiper.isEnd;
      this.$emit('active-index-change', this.isEnd, this.swiper.activeIndex + 1);
    },
    atFirstPage() {
      return !this.swiper || this.isBeginning;
    },
    atLastPage() {
      return this.swiper ? this.isEnd : false;
    },
    update() {
      if (this.swiper) this.swiper.update();
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.swiper-wrapper) {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  align-items: stretch;
  transition-property: transform;
  box-sizing: content-box;
}

.swiper-slide {
  align-items: stretch;
  vertical-align: middle;
  border-radius: 10px;
  display: flex;
  transition: all 0.4s ease;
  flex-shrink: 0;
  width: auto;
  max-width: 100%;
  box-sizing: border-box;
}

.swiper-slide:last-child {
  margin-right: 12px;
}

.swiper-container {
  list-style: none;
  z-index: 1;
  overflow-x: hidden;
  overflow-y: visible;
}

// Swiper's own stylesheet is not loaded, so its auto-height rules live here: slides keep their
// own height instead of stretching to the tallest, and the wrapper's height animates alongside
// the slide (Swiper sets the duration), pushing the content below up and down smoothly.
.base-swiper-autoheight :deep(.swiper-wrapper) {
  align-items: flex-start;
  transition-property: transform, height;
}

.swiper-no-swipe-back :deep(.swiper) {
  touch-action: pan-y pinch-zoom;
}

.swiper-no-swipe-back :deep(.swiper-wrapper) {
  touch-action: pan-y pinch-zoom;
}
</style>
